// Small, dependency-free helpers shared by the screen recording components.

/** Monotonic-ish unique id, good enough for client-side clip/voiceover keys. */
export const makeId = (prefix = 'id') =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

/** mm:ss (or h:mm:ss once past an hour) formatter for timers / trim labels. */
export const formatTime = (totalSeconds = 0) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  const pad = (n) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`
}

/** Picks the best MediaRecorder mimeType this browser actually supports. */
export const pickSupportedMimeType = (candidates) => {
  const list = candidates || [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=h264,opus',
    'video/webm',
  ]
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return list[list.length - 1]
  }
  return list.find((t) => MediaRecorder.isTypeSupported(t)) || list[list.length - 1]
}

/**
 * MediaRecorder-produced webm blobs often report `duration: Infinity` in
 * Chrome until you seek past the end once. This resolves the *real*
 * duration by seeking to a huge timestamp and reading back where the
 * browser actually clamps to, then rewinds to 0. Resolves 0 if it can't
 * be determined (caller should treat that as "unknown", not "empty").
 */
export const readVideoDuration = (videoEl) =>
  new Promise((resolve) => {
    const finish = (d) => resolve(Number.isFinite(d) && d > 0 ? d : 0)

    if (Number.isFinite(videoEl.duration) && videoEl.duration > 0) {
      finish(videoEl.duration)
      return
    }

    const onTimeUpdate = () => {
      videoEl.removeEventListener('timeupdate', onTimeUpdate)
      const d = videoEl.duration
      videoEl.currentTime = 0
      finish(Number.isFinite(d) ? d : 0)
    }

    videoEl.addEventListener('timeupdate', onTimeUpdate)
    try {
      videoEl.currentTime = 1e9
    } catch {
      videoEl.removeEventListener('timeupdate', onTimeUpdate)
      finish(0)
    }

    // Safety timeout in case the browser never fires timeupdate for this.
    setTimeout(() => {
      videoEl.removeEventListener('timeupdate', onTimeUpdate)
      finish(0)
    }, 4000)
  })

/** Grabs a JPEG thumbnail from a <video> element at its current frame. */
export const captureVideoThumbnail = (videoEl, maxWidth = 320) => {
  try {
    const ratio = videoEl.videoHeight && videoEl.videoWidth ? videoEl.videoHeight / videoEl.videoWidth : 0.5625
    const w = Math.min(maxWidth, videoEl.videoWidth || maxWidth)
    const h = Math.round(w * ratio)
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    ctx.drawImage(videoEl, 0, 0, w, h)
    return canvas.toDataURL('image/jpeg', 0.7)
  } catch {
    return null
  }
}

/**
 * Best-effort check for whether a loaded <video> actually has an audio
 * track. Only Chrome/Edge expose the (non-standard but widely supported)
 * `audioTracks` list; Firefox exposes `mozHasAudio` instead; everything
 * else has no reliable way to tell before playback starts. Defaults to
 * `true` (rather than guessing `false`) when neither is available, since a
 * false negative disables audio editing entirely while a false positive
 * just surfaces a clear "video engine rejected this edit" error later.
 */
const detectHasAudioTrack = (videoEl) => {
  try {
    if (typeof videoEl.audioTracks !== 'undefined') return videoEl.audioTracks.length > 0
    if (typeof videoEl.mozHasAudio !== 'undefined') return videoEl.mozHasAudio
  } catch { /* noop */ }
  return true
}

/**
 * Loads `src` (a blob: object URL or a plain http(s) URL — anything a
 * <video> element can play) into an offscreen <video>, and resolves its
 * duration, a thumbnail, and a best-effort "does it have audio" guess.
 * Shared by `inspectVideoBlob` (a freshly recorded/edited clip) and
 * `inspectVideoFromUrl` (an already-published recording being reopened for
 * editing) so both end up with the same clip shape.
 */
const inspectVideoSrc = (src, { crossOrigin } = {}) =>
  new Promise((resolve) => {
    const video = document.createElement('video')
    video.muted = true
    video.preload = 'metadata'
    // Needed to read the thumbnail canvas back out for a cross-origin URL
    // (e.g. the S3-hosted file of an already-published recording). Harmless
    // if the host doesn't send CORS headers — we just fall back to no
    // thumbnail (captureVideoThumbnail already catches that) rather than
    // failing to load the video itself.
    if (crossOrigin) video.crossOrigin = crossOrigin
    video.src = src

    const onLoaded = async () => {
      const durationSec = await readVideoDuration(video)
      const hasAudio = detectHasAudioTrack(video)
      // Seek briefly into the clip so the thumbnail isn't a black first frame.
      const seekTo = Math.min(0.5, durationSec || 0)
      const grab = () => {
        const thumbnailDataUrl = captureVideoThumbnail(video)
        video.removeEventListener('seeked', grab)
        resolve({ url: src, durationSec, thumbnailDataUrl, hasAudio })
      }
      if (seekTo > 0) {
        video.addEventListener('seeked', grab, { once: true })
        try { video.currentTime = seekTo } catch { grab() }
      } else {
        grab()
      }
    }

    video.addEventListener('loadedmetadata', onLoaded, { once: true })
    video.addEventListener('error', () => resolve({ url: src, durationSec: 0, thumbnailDataUrl: null, hasAudio: true }), { once: true })
  })

/** Builds a video element from a blob, loads its metadata, resolves duration + a thumbnail. */
export const inspectVideoBlob = (blob) => inspectVideoSrc(URL.createObjectURL(blob))

/**
 * Same as `inspectVideoBlob`, but for a video already reachable at a plain
 * URL (e.g. a documentation entry's existing, already-published S3 URL)
 * rather than a local Blob — used to let the studio reopen a published
 * recording for editing instead of only being able to start a fresh one.
 */
export const inspectVideoFromUrl = (url) => inspectVideoSrc(url, { crossOrigin: 'anonymous' })

/** Stops every track on a MediaStream (screen share, mic, mixed output, …). */
export const stopStream = (stream) => {
  if (!stream) return
  stream.getTracks().forEach((t) => {
    try { t.stop() } catch { /* noop */ }
  })
}

export const isScreenCaptureSupported = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getDisplayMedia === 'function' &&
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder !== 'undefined'
